import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collectionGroup,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { AccountJobDoc } from '@/lib/types';

interface JobRow extends AccountJobDoc {
  id: string;
  /** Path to the doc, used as a unique React key. */
  path: string;
}

const STATUS_CLS: Record<AccountJobDoc['status'], string> = {
  queued: 'bg-slate-100 text-slate-700',
  running: 'bg-sky-100 text-sky-800',
  done: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
};

function StatusChip({ status }: { status: AccountJobDoc['status'] }) {
  return (
    <span className={`chip ${STATUS_CLS[status]}`}>{status}</span>
  );
}

function fmtRelative(date: Date | null): string {
  if (!date) return '—';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function RecentAnalysesPanel() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collectionGroup(db, 'jobs'),
      orderBy('updatedAt', 'desc'),
      limit(20),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setJobs(
          snap.docs.map((d) => {
            const data = d.data() as AccountJobDoc;
            // Doc path is `accounts/{accountId}/jobs/{jobId}`. We trust the
            // stored `accountId` field and use the path only as a stable key.
            return {
              ...data,
              id: d.id,
              path: d.ref.path,
            };
          }),
        );
        setLoaded(true);
      },
      (err) => {
        setError(err.message);
        setLoaded(true);
      },
    );
    return unsub;
  }, []);

  const tally = useMemo(() => {
    const t = { total: jobs.length, done: 0, failed: 0, running: 0, queued: 0 };
    for (const j of jobs) {
      t[j.status] += 1;
    }
    return t;
  }, [jobs]);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Recent account analyses
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Last {tally.total || 20} jobs across all accounts (Re-analyze, Pull
            new calls).
          </p>
        </div>
        {tally.total > 0 && (
          <div className="flex items-center gap-3 text-xs">
            <span className="text-emerald-700">
              <span className="font-semibold tabular-nums">{tally.done}</span> done
            </span>
            {tally.running > 0 && (
              <span className="text-sky-700">
                <span className="font-semibold tabular-nums">
                  {tally.running}
                </span>{' '}
                running
              </span>
            )}
            {tally.queued > 0 && (
              <span className="text-slate-600">
                <span className="font-semibold tabular-nums">{tally.queued}</span>{' '}
                queued
              </span>
            )}
            {tally.failed > 0 && (
              <span className="text-rose-700">
                <span className="font-semibold tabular-nums">{tally.failed}</span>{' '}
                failed
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-slate-600">
                Account
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-slate-600">
                Kind
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-slate-600">
                Status
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-slate-600">
                Updated
              </th>
              <th className="px-4 py-2 font-medium text-slate-600">Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {!loaded && (
              <tr>
                <td className="px-4 py-6 text-slate-500" colSpan={5}>
                  Loading…
                </td>
              </tr>
            )}
            {loaded && jobs.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-slate-500" colSpan={5}>
                  No analyses recorded yet.
                </td>
              </tr>
            )}
            {jobs.map((j) => {
              const updated = j.updatedAt?.toDate() ?? null;
              const kind = j.pullNew ? 'Pull new calls' : 'Re-analyze';
              return (
                <tr key={j.path}>
                  <td className="whitespace-nowrap px-4 py-2">
                    <Link
                      to={`/accounts/${j.accountId}`}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {j.accountId}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-600">
                    {kind}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    <StatusChip status={j.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-600">
                    <span title={updated?.toLocaleString() ?? ''}>
                      {fmtRelative(updated)}
                    </span>
                  </td>
                  <td className="max-w-md px-4 py-2 text-xs">
                    {j.error ? (
                      <span
                        className="line-clamp-3 text-rose-700"
                        title={j.error}
                      >
                        {j.error}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
